import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import {
  eurosToMicros,
  microsToEuros,
  useAdminBillingPrices,
  useCreateAdminBillingPrice,
  useDeleteAdminBillingPrice,
} from '../../hooks/use-admin-billing'

interface Props {
  /** The organization whose own rows are managed. Without it, the list price rows. */
  orgUid?: string
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

/**
 * BillingPricesSection shows the price in effect for every code and the price
 * rows of one organization (or the list price), with add and delete (spec
 * 2026-10-05-13). A row is never edited: a change is a new row with a later month.
 */
export function BillingPricesSection({ orgUid }: Props) {
  const { t, i18n } = useTranslation('admin')
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'
  const scope = orgUid ? 'org' : 'list'
  const { data, isLoading, error } = useAdminBillingPrices(orgUid)
  const create = useCreateAdminBillingPrice()
  const remove = useDeleteAdminBillingPrice()
  const [code, setCode] = useState('fixed_fee')
  const [price, setPrice] = useState('')
  const [period, setPeriod] = useState<'month' | 'year'>('month')
  const [month, setMonth] = useState(currentMonth())
  const [formError, setFormError] = useState<string | null>(null)

  const codes = data?.codes ?? []
  const label = (c: string) => {
    const found = codes.find((x) => x.code === c)
    return found ? (found.label[lang] ?? found.label.en) : c
  }
  const unit = (c: string) => {
    const found = codes.find((x) => x.code === c)
    return found ? `${found.billing_unit}` : ''
  }

  const perUnit = (c: string) =>
    unit(c) === 'month' ? t('billing.prices.per_month') : t('billing.prices.per_unit', { unit: unit(c) })

  const submit = () => {
    const micros = eurosToMicros(price)
    if (micros === null || !month) {
      setFormError(t('billing.prices.invalid'))
      return
    }
    setFormError(null)
    create.mutate(
      { body: { organization_uid: orgUid ?? null, code, unit_price_micros: micros, period, effective_from: `${month}-01` } },
      {
        onSuccess: () => setPrice(''),
        onError: () => setFormError(t('billing.prices.save_error')),
      },
    )
  }

  return (
    <section className="space-y-4" data-testid={`billing-prices-${scope}`}>
      <div>
        <h2 className="text-lg font-semibold">{orgUid ? t('billing.prices.org_title') : t('billing.prices.list_title')}</h2>
        <p className="text-sm text-muted-foreground">{orgUid ? t('billing.prices.org_help') : t('billing.prices.list_help')}</p>
      </div>

      {error && <div className="text-sm text-destructive">{t('billing.load_error')}</div>}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <Table data-testid={`billing-in-effect-${scope}`}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('billing.prices.column.code')}</TableHead>
              <TableHead className="text-right">{t('billing.prices.column.in_effect')}</TableHead>
              <TableHead>{t('billing.prices.column.since')}</TableHead>
              <TableHead>{t('billing.prices.column.source')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.in_effect ?? []).map((e) => (
              <TableRow key={e.code} data-testid={`billing-in-effect-${scope}-${e.code}`}>
                <TableCell>
                  {label(e.code)}
                  <div className="text-xs text-muted-foreground">{perUnit(e.code)}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {microsToEuros(e.unit_price_micros, i18n.language)}
                  <div className="text-xs text-muted-foreground" data-testid={`billing-in-effect-${scope}-${e.code}-period`}>
                    {t(`billing.prices.period.${e.period}`)}
                  </div>
                  {e.period === 'year' && (
                    <div className="text-xs text-muted-foreground" data-testid={`billing-in-effect-${scope}-${e.code}-monthly`}>
                      {t('billing.prices.monthly_equivalent', { price: microsToEuros(e.monthly_equivalent_micros, i18n.language) })}
                    </div>
                  )}
                </TableCell>
                <TableCell>{e.effective_from?.slice(0, 7) ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{t(`billing.prices.source.${e.source}`)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('billing.loading')}</div>
        ) : (data?.data ?? []).length === 0 ? (
          <div className="px-6 py-4 text-center text-muted-foreground" data-testid={`billing-prices-${scope}-empty`}>
            {t('billing.prices.empty')}
          </div>
        ) : (
          <Table data-testid={`billing-price-rows-${scope}`}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.prices.column.code')}</TableHead>
                <TableHead className="text-right">{t('billing.prices.column.price')}</TableHead>
                <TableHead>{t('billing.prices.column.from')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.data ?? []).map((r) => (
                <TableRow key={r.uid} data-testid={`billing-price-row-${scope}-${r.code}-${r.effective_from.slice(0, 7)}`}>
                  <TableCell>{label(r.code)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {microsToEuros(r.unit_price_micros, i18n.language)} {t(`billing.prices.period.${r.period}`)}
                  </TableCell>
                  <TableCell>{r.effective_from.slice(0, 7)}</TableCell>
                  <TableCell className="text-right">
                    <DeleteConfirmDialog
                      title={t('billing.prices.delete_title')}
                      description={t('billing.prices.delete_description')}
                      onConfirm={() => remove.mutate({ params: { path: { uid: r.uid } } })}
                      isDeleting={remove.isPending}
                      trigger={
                        <Button variant="ghost" size="sm" data-testid={`billing-price-delete-${scope}-${r.code}-${r.effective_from.slice(0, 7)}`}>
                          {t('billing.prices.delete')}
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid={`billing-price-form-${scope}`}>
        <div className="space-y-1">
          <Label>{t('billing.prices.column.code')}</Label>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger className="w-60" data-testid={`billing-price-code-${scope}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {codes.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.label[lang] ?? c.label.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('billing.prices.period_label')}</Label>
          <Select value={period} onValueChange={(v) => setPeriod(v as 'month' | 'year')}>
            <SelectTrigger className="w-40" data-testid={`billing-price-period-${scope}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">{t('billing.prices.period.month')}</SelectItem>
              <SelectItem value="year">{t('billing.prices.period.year')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('billing.prices.price_label', { per: perUnit(code) })}</Label>
          <Input
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="0.125"
            className="w-36"
            data-testid={`billing-price-value-${scope}`}
          />
        </div>
        <div className="space-y-1">
          <Label>{t('billing.prices.column.from')}</Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44"
            data-testid={`billing-price-month-${scope}`}
          />
        </div>
        <Button onClick={submit} disabled={create.isPending} data-testid={`billing-price-add-${scope}`}>
          {t('billing.prices.add')}
        </Button>
        {formError && (
          <div className="w-full text-sm text-destructive" data-testid={`billing-price-error-${scope}`}>
            {formError}
          </div>
        )}
      </div>
    </section>
  )
}
